import { useState, useEffect, memo } from "react";
import { Cookie, X, Settings, Shield, Check } from "lucide-react";

const COOKIE_CONSENT_KEY = "george_energy_cookie_consent";
const CONSENT_VERSION = "1.0"; // Increment this when policy changes to re-ask users

export const CookieConsentBanner = memo(() => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState({
    essential: true, // Always true, cannot be changed
    analytics: false,
  });

  // Check for existing consent on mount
  useEffect(() => {
    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    
    if (storedConsent) {
      try {
        const parsed = JSON.parse(storedConsent);
        // Check if consent version matches
        if (parsed.version === CONSENT_VERSION) {
          setConsent(parsed.consent);
          // Apply consent immediately
          applyConsent(parsed.consent);
          return;
        }
      } catch (e) {
        // Invalid stored consent, show banner
      }
    }
    
    // No valid consent found, show banner after a short delay
    const timer = setTimeout(() => {
      setShowBanner(true);
    }, 1500); // Show after loading screen
    
    return () => clearTimeout(timer);
  }, []);

  // Listen for "openCookieSettings" event from footer button
  useEffect(() => {
    const handleOpenSettings = () => {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      setShowDetails(false); // Reset to main view
      setShowBanner(true);
    };
    
    window.addEventListener('openCookieSettings', handleOpenSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenSettings);
  }, []);

  // Apply consent settings
  const applyConsent = (consentSettings) => {
    if (consentSettings.analytics) {
      // Enable PostHog tracking
      if (window.posthog) {
        window.posthog.opt_in_capturing();
      }
    } else {
      // Disable PostHog tracking
      if (window.posthog) {
        window.posthog.opt_out_capturing();
      }
    }
  };

  // Save consent and apply
  const saveConsent = (consentSettings) => {
    const consentData = {
      version: CONSENT_VERSION,
      consent: consentSettings,
      timestamp: new Date().toISOString(),
    };
    
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    setConsent(consentSettings);
    applyConsent(consentSettings);
    setShowBanner(false);
  };

  // Accept all cookies
  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
    });
  };

  // Reject non-essential cookies
  const rejectNonEssential = () => {
    saveConsent({
      essential: true,
      analytics: false,
    });
  };

  // Save custom preferences
  const savePreferences = () => {
    saveConsent(consent);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] p-4 sm:p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-400/50 overflow-hidden">
        {/* Main Banner */}
        <div className="p-4 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
              <Cookie className="w-6 h-6 text-emerald-600" />
            </div>
            
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-emerald-950 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Cookie Preferences
              </h3>
              <p className="text-emerald-700 text-sm leading-relaxed">
                We use cookies to enhance your experience. Essential cookies are necessary for the website to function. 
                Analytics cookies help us improve our services. You can choose which cookies to allow.
              </p>
              
              {/* Privacy Policy Link */}
              <button 
                onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'privacy' }))}
                className="text-sm text-lime-600 hover:text-lime-700 underline mt-2 inline-block"
              >
                Read our Privacy Policy
              </button>
            </div>
            
            {/* Close button (rejects non-essential) */}
            <button 
              onClick={rejectNonEssential}
              className="flex-shrink-0 p-2 hover:bg-emerald-50 rounded-full transition-colors"
              aria-label="Close and reject non-essential cookies"
            >
              <X className="w-5 h-5 text-emerald-700" />
            </button>
          </div>
          
          {/* Cookie Details (Expandable) */}
          {showDetails && (
            <div className="mt-6 pt-6 border-t border-gray-400/40 space-y-4">
              {/* Essential Cookies */}
              <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="font-medium text-emerald-900">Essential Cookies</p>
                    <p className="text-sm text-emerald-600">Required for website functionality</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-emerald-600">Always Active</span>
                  <div className="w-12 h-6 bg-emerald-500 rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
              
              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="font-medium text-emerald-900">Analytics Cookies</p>
                    <p className="text-sm text-emerald-600">Help us improve our website (PostHog)</p>
                  </div>
                </div>
                <button 
                  onClick={() => setConsent(prev => ({ ...prev, analytics: !prev.analytics }))}
                  className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                    consent.analytics ? 'bg-emerald-500 justify-end' : 'bg-gray-300 justify-start'
                  }`}
                  aria-label={consent.analytics ? "Disable analytics cookies" : "Enable analytics cookies"}
                >
                  <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                </button>
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            {!showDetails ? (
              <>
                <button
                  onClick={rejectNonEssential}
                  className="flex-1 px-4 py-2.5 border border-emerald-300 text-emerald-700 rounded-xl hover:bg-emerald-50 transition-colors font-medium text-sm"
                  data-testid="cookie-reject-btn"
                >
                  Reject Non-Essential
                </button>
                <button
                  onClick={() => setShowDetails(true)}
                  className="flex-1 px-4 py-2.5 border border-emerald-300 text-emerald-700 rounded-xl hover:bg-emerald-50 transition-colors font-medium text-sm flex items-center justify-center gap-2"
                  data-testid="cookie-customize-btn"
                >
                  <Settings className="w-4 h-4" />
                  Customize
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium text-sm flex items-center justify-center gap-2"
                  data-testid="cookie-accept-btn"
                >
                  <Check className="w-4 h-4" />
                  Accept All
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowDetails(false)}
                  className="flex-1 px-4 py-2.5 border border-emerald-300 text-emerald-700 rounded-xl hover:bg-emerald-50 transition-colors font-medium text-sm"
                >
                  Back
                </button>
                <button
                  onClick={savePreferences}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium text-sm flex items-center justify-center gap-2"
                  data-testid="cookie-save-btn"
                >
                  <Check className="w-4 h-4" />
                  Save Preferences
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

// Cookie Settings Button (for footer)
export const CookieSettingsButton = memo(() => {
  const openCookieSettings = () => {
    // Dispatch event to show cookie banner without page reload
    window.dispatchEvent(new CustomEvent('openCookieSettings'));
  };

  return (
    <button
      onClick={openCookieSettings}
      className="flex items-center gap-1.5 text-emerald-700 hover:text-lime-600 transition-colors text-sm"
      title="Manage cookie preferences"
      data-testid="cookie-settings-btn"
    >
      <Cookie className="w-4 h-4" />
      <span>Cookie Settings</span>
    </button>
  );
});
